'use client';

import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import ProgramShowingListSkeleton from '@/app/[locale]/(default-layout)/program/_components/skeletons/ProgramShowingListSkeleton';
import TicketButtonGrouped from '@/app/[locale]/(default-layout)/program/_components/TicketButtonGrouped';

import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { Client } from '@cineamo/api/constants/Client.enum';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { useAgeRatingText } from '@cineamo/ui/src/helpers/movie-helper';
import { formatDuration, getGroupedShowings } from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import useShowingsListFiltered from '@/hooks/useShowingsListFiltered';
import { useVirtualPagination } from '@/hooks/useVirtualPagination';

import { IconBack24 } from '@cineamo/ui/src/icons/ic-back';
import { IconClose24 } from '@cineamo/ui/src/icons/ic-close';
import { IconInfo24 } from '@cineamo/ui/src/icons/ic-info';
import { IconRightArrow16 } from '@cineamo/ui/src/icons/ic-rightArrow';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { getPageBackgroundStyle } from '@/utils/page-background';

type ProgramCompactGroupedListProps = {
    contents: CachedContentData[];
    selectedDate?: string | 'all';
    showingsCountForContentId: Map<ContentDto['id'], number>;
    /**
     * How the showtimes of the selected movie are presented. `overlay` slides a full-height panel in
     * over the page, which is the only sensible option on a phone. `split` docks the panel beside
     * the list in a second column — the treatment the web app's desktop program uses, and pointless
     * below about a thousand pixels of width.
     */
    variant?: 'overlay' | 'split';
};

const VIRTUAL_PAGINATION_CONFIG = {
    initialLoad: 10,
    loadMore: 10,
    loadDebounce: 100,
    checkInterval: 200,
    rootMargin: '200px'
} as const;

const PANEL_TRANSITION_MS = 300;

type ProgramCompactGroupedListItemProps = {
    content: CachedContentData;
    totalItems: number;
    onSelect: () => void;
    /** Marks the row whose showtimes the docked panel is showing. Unused in the overlay variant,
     *  where the panel covers the list anyway. */
    isSelected?: boolean;
};

function ProgramCompactGroupedListItem(props: ProgramCompactGroupedListItemProps): React.JSX.Element {
    const { content, totalItems, onSelect, isSelected } = props;

    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();

    return (
        <button
            type="button"
            className="relative w-full flex flex-row items-center gap-x-12 py-12 text-left cursor-pointer"
            aria-current={isSelected || undefined}
            aria-label={content.title}
            onClick={onSelect}>
            {/* The selection tint bleeds past the row's edges without taking part in layout. Doing
                it with padding and a negative margin instead shifted the row's contents inward
                wherever the container refused to let the row grow, such as the split view's grid. */}
            {isSelected && (
                <span
                    className="absolute inset-y-0 -inset-x-8 rounded-8 pointer-events-none bg-[rgba(var(--on-background-color-rgb),0.06)]"
                    aria-hidden
                />
            )}
            <ImageWithTransition
                className="rounded-2 flex-none"
                width={44}
                height={66}
                src={content.posterImage ?? ''}
                alt={content.title ?? ''}
            />
            <div className="min-w-0 flex-1 flex flex-col gap-y-2">
                <span className="text-body-bold-16 tracking-[0.8px] truncate">{content.title}</span>
                <DotSeparatedLabel
                    className="text-body-12 text-[rgba(var(--on-background-color-rgb),0.8)]"
                    labels={[domainConfiguration.cinema.name, t('specials.n-showings', { count: totalItems })]}
                />
            </div>
            <IconRightArrow16 className="flex-none opacity-70" />
        </button>
    );
}

type PanelInsets = {
    top: number;
    bottom: number;
};

type ProgramCompactShowtimesPanelProps = {
    content: CachedContentData;
    selectedDate?: string | 'all';
    isVisible: boolean;
    insets: PanelInsets;
    onClose: () => void;
    /** Docked beside the list instead of sliding in over the page. See the list's `variant`. */
    isDocked?: boolean;
};

function ProgramCompactShowtimesPanel(props: ProgramCompactShowtimesPanelProps): React.JSX.Element {
    const { content, selectedDate, isVisible, insets, onClose, isDocked } = props;

    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const styleBackground = getPageBackgroundStyle(domainConfiguration.style);
    const ageRatingText = useAgeRatingText(content.ageRating, domainConfiguration.cinema.countryCode);

    const { showings } = useShowingsListFiltered({
        contentId: content.id,
        selectedDate: selectedDate
    });

    const groupedShowings = useMemo(() => {
        return getGroupedShowings(showings ?? [], Client.CinemaWebsite).toSorted(
            (a, b) => new Date(a.startDatetime ?? '').getTime() - new Date(b.startDatetime ?? '').getTime()
        );
    }, [showings]);

    const subtitleLabels = [ageRatingText, content.runtime ? formatDuration(content.runtime, t) : undefined].filter(
        (label): label is string => !!label
    );

    const contentHref: HrefOrHrefWithParams<'/program/[contentId]'> = {
        pathname: '/program/[contentId]',
        params: {
            contentId: content.id
        }
    };

    // Lock the page scroll behind the panel while it is shown (released during the slide-out). A
    // docked panel is part of the page rather than on top of it, so it must not lock anything.
    useEffect(() => {
        if (!isVisible || isDocked) {
            return;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isVisible, isDocked]);

    return (
        <div
            className={cn(
                'flex flex-col',
                isDocked
                    ? 'sticky top-0 max-h-screen overflow-y-auto rounded-8'
                    : [
                          'fixed left-0 right-0 z-40 overflow-y-auto transition-transform duration-300 ease-out',
                          isVisible ? 'translate-x-0' : 'translate-x-full'
                      ]
            )}
            style={isDocked ? undefined : { ...styleBackground, top: insets.top, bottom: 0 }}
            role={isDocked ? 'region' : 'dialog'}
            aria-label={content.title}>
            <div
                className="sticky top-0 z-10 flex flex-row items-center gap-x-12 px-16 py-12"
                style={styleBackground}>
                <button
                    type="button"
                    className="flex-none cursor-pointer"
                    aria-label={isDocked ? t('common.close') : t('common.back-to-program')}
                    onClick={onClose}>
                    {isDocked ? <IconClose24 /> : <IconBack24 />}
                </button>
                <div className="min-w-0 flex-1 flex flex-col items-center gap-y-2">
                    <span className="text-body-bold-16 tracking-[0.8px] truncate max-w-full">{content.title}</span>
                    {subtitleLabels.length > 0 && (
                        <DotSeparatedLabel
                            className="text-body-12 text-[rgba(var(--on-background-color-rgb),0.8)]"
                            labels={subtitleLabels}
                        />
                    )}
                </div>
                <Link
                    className="flex-none"
                    href={contentHref}
                    aria-label={content.title}
                    prefetch={false}>
                    <IconInfo24 className="opacity-70" />
                </Link>
            </div>
            <div
                className="flex flex-col gap-y-12 px-16 pt-12"
                style={{ paddingBottom: (isDocked ? 0 : insets.bottom) + 24 }}>
                {groupedShowings.map((groupedShowing) => (
                    <TicketButtonGrouped
                        key={`ProgramCompactShowtime#${groupedShowing.showings[0].id}`}
                        className="w-full h-[132px] text-[var(--on-surface-color)]"
                        groupedShowing={groupedShowing}
                        content={content}
                        showTags
                        showDateAndTime
                    />
                ))}
            </div>
        </div>
    );
}

export function ProgramCompactGroupedList(props: ProgramCompactGroupedListProps): React.JSX.Element {
    const { contents, selectedDate } = props;
    const isSplit = props.variant === 'split';

    const { visibleCount, loadMoreRef, hasMore } = useVirtualPagination({
        items: contents,
        config: VIRTUAL_PAGINATION_CONFIG
    });

    const [selectedContent, setSelectedContent] = useState<CachedContentData | null>(null);
    const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
    const [panelInsets, setPanelInsets] = useState<PanelInsets>({ top: 0, bottom: 0 });
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    function openPanel(content: CachedContentData) {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        // A docked panel neither slides nor covers anything, so none of the inset measuring or the
        // two-frame delay below applies — it simply appears in the second column.
        if (isSplit) {
            setSelectedContent(content);
            setIsPanelVisible(true);
            return;
        }
        // The panel only covers the page content: it starts below the header/sticky banners still
        // in view (the page scroll is locked while open) and ends above the fixed bottom navigation.
        const stickyTopElements = [
            document.querySelector('nav'),
            document.getElementById('__cineamo_app_banner__'),
            document.getElementById('__info_banner__')
        ];
        setPanelInsets({
            top: Math.max(
                0,
                ...stickyTopElements.map((element) => (element ? element.getBoundingClientRect().bottom : 0))
            ),
            bottom: document.getElementById('mobile-nav')?.offsetHeight ?? 0
        });
        setSelectedContent(content);
        // Mount the panel off-canvas first so the slide-in transition can run
        requestAnimationFrame(() => requestAnimationFrame(() => setIsPanelVisible(true)));
    }

    /** Selecting the movie whose showtimes are already open closes the panel again, as on the web
     *  app — otherwise the row offers no way back once it has been clicked. */
    function togglePanel(content: CachedContentData) {
        if (selectedContent?.id === content.id && isPanelVisible) {
            closePanel();
            return;
        }
        openPanel(content);
    }

    function closePanel() {
        setIsPanelVisible(false);
        if (isSplit) {
            setSelectedContent(null);
            return;
        }
        closeTimeoutRef.current = setTimeout(() => setSelectedContent(null), PANEL_TRANSITION_MS);
    }

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    const listElement = (
        <div className="flex flex-col">
            {contents.slice(0, visibleCount).map((content) => (
                <ProgramCompactGroupedListItem
                    key={`ProgramCompactGroupedListItem#${content.id}`}
                    content={content}
                    totalItems={props.showingsCountForContentId.get(content.id) ?? 0}
                    isSelected={isSplit && selectedContent?.id === content.id}
                    onSelect={() => togglePanel(content)}
                />
            ))}
            {hasMore && (
                <div
                    className="loader-element"
                    ref={loadMoreRef}>
                    <ProgramShowingListSkeleton
                        items={1}
                        selectedDate={selectedDate}
                    />
                </div>
            )}
        </div>
    );

    if (isSplit) {
        return (
            // Two columns only once something is selected, so the list uses the full width until
            // then instead of leaving half the page empty.
            <div className={cn('grid gap-x-24 items-start', selectedContent && 'grid-cols-2')}>
                {listElement}
                {selectedContent && (
                    <ProgramCompactShowtimesPanel
                        content={selectedContent}
                        selectedDate={selectedDate}
                        isVisible={isPanelVisible}
                        insets={panelInsets}
                        onClose={closePanel}
                        isDocked
                    />
                )}
            </div>
        );
    }

    return (
        <>
            {listElement}
            {selectedContent && (
                <ProgramCompactShowtimesPanel
                    content={selectedContent}
                    selectedDate={selectedDate}
                    isVisible={isPanelVisible}
                    insets={panelInsets}
                    onClose={closePanel}
                />
            )}
        </>
    );
}

export default ProgramCompactGroupedList;
